import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Image from 'next/image';
import * as React from 'react';
import { useMemo } from 'react';
import CheckTokenContainer from '../components/CheckTokenContainer';
import ClaimErrorContainer from '../components/ClaimErrorContainer';
import ConnectExistingWalletContainer from '../components/ConnectExistingWallet';
import ConnectWalletContainer from '../components/ConnectWalletContainer';
import ErrorContainer from '../components/ErrorContainer';
import LoadingContainer from '../components/LoadingContainer';
import NotEligibleTokenContainer from '../components/NotEligibleTokenGating';
import PaperWalletOTPContainer from '../components/PaperWalletOTPContainer';
import PayWithGasContainer from '../components/PayWithGasContainer';
import RecoveryCodeContainer from '../components/RecoveryCodeContainer';
import SuccessContainer from '../components/SuccessContainer';
import { TabPanel, TabSwitcher } from '../components/TabSwitcher';

import { ClaimContainerType, ClaimModalLayoutProps } from '../interface';

const ClaimModalLayout = ({
  open,
  claimStatus,
  currentModalState,
  error,
  address,
  email,
  otp,
  loaderTitle,
  loaderDescription,
  recoveryCode,
  walletOptions,
  logoUrl,
  viewTokenTrackerURL,
  tokenTrackerURL,
  termsUrl,
  privacyUrl,
  content,
  tokenName,
  tokenGatingName,
  tokenGatingError,
  showDisconnectButton,
  showBuyButton,
  price,
  showLoader,
  validation,
  networkName,
  currency,
  onClickSwitchNetwork,
  onClickDoNotHaveWallet,
  onChangeEmail,
  onChangeOTP,
  onChangeRecoveryCode,
  onClickMetamask,
  onClickWalletConnect,
  onClickContinueWithEmail,
  onClickRecoveryCode,
  onClickExistingWallet,
  onCloseModal,
  handleSuccessRedirection,
  handleRetry,
  onClickContinueCheckToken,
  onClickBuyToken,
  onClickDisconnectWallet,
  onClickClaimWithGas,
  onClickContinueWithSocial,
  onClickSkip
}: ClaimModalLayoutProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const mobileScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const isLoading = useMemo(() => {
    return currentModalState === ClaimContainerType.LOADING || currentModalState === ClaimContainerType.ERROR;
  }, [currentModalState]);

  return (
    <Dialog
      open={open}
      onClose={onCloseModal}
      fullScreen={fullScreen}
      id="wallet-connect-modal"
      maxWidth="sm"
      PaperProps={{
        sx: {
          minWidth: '100px',
          zIndex: 12,
          maxHeight: 'calc(100% - 50px)',
          overflowY: 'auto',
          overflowX: 'hidden',
          top: '0',
          height: 'unset',
          margin: '0 6px'
        }
      }}
      sx={{
        zIndex: 100,
        '& .MuiDialog-container': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'fixed',
          width: '100%',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          height: '100dvh'
        },
        '& .MuiPaper-root': {
          height: 'auto',
          border: '1px solid #707b7c'
        }
      }}
    >
      <TabSwitcher value={currentModalState}>
        {isLoading ? (
          <Box
            sx={{
              padding: { lg: '40px', xs: '10px', md: '30px' },
              minHeight: { xs: '100px', lg: 0 },
              width: !fullScreen ? '500px' : null
            }}
          >
            <TabPanel id={ClaimContainerType.LOADING}>
              <LoadingContainer
                description={loaderDescription || content?.LoaderContent?.description}
                loaderTitle={loaderTitle}
              />
            </TabPanel>
            <TabPanel id={ClaimContainerType.ERROR}>
              <ErrorContainer errorMessage={error} handleRetry={handleRetry} />
            </TabPanel>
          </Box>
        ) : (
          <Box
            sx={{
              width: mobileScreen ? '100%' : !fullScreen ? '500px' : null
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end'
              }}
            >
              <CloseIcon
                sx={{
                  cursor: 'pointer',
                  padding: '10px'
                }}
                onClick={() => onCloseModal(currentModalState)}
              />
            </Box>
            <Box
              sx={{
                padding: {
                  lg: '10px 40px 40px 40px',
                  xs: '10px 10px 20px 10px',
                  md: '10px 40px 40px 40px'
                }
              }}
            >
              {!address && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  <Image
                    src={content?.walletLogo || ''}
                    width={152}
                    height={48}
                    style={{
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      maxHeight: '48px',
                      maxWidth: '152px',
                      cursor: 'pointer',
                      padding: '40px 0'
                    }}
                    alt="logo"
                  />
                </Box>
              )}
              <TabPanel id={ClaimContainerType.SUCCESS}>
                <SuccessContainer
                  claimStatus={claimStatus}
                  deliveryWallet={address}
                  onClickSuccess={handleSuccessRedirection}
                  viewTokenTrackerURL={viewTokenTrackerURL}
                  tokenTrackerURL={tokenTrackerURL}
                  content={content?.SuccessContent}
                />
              </TabPanel>
              {walletOptions.enableEmailWallet && (
                <TabPanel id={ClaimContainerType.CONNECT_WALLET}>
                  <ConnectWalletContainer
                    content={content?.ConnectWalletContent}
                    email={email}
                    onChangeEmail={onChangeEmail}
                    error={error}
                    onClickContinueWithEmail={onClickContinueWithEmail}
                    onClickExistingWallet={onClickExistingWallet}
                    onClickContinueWithSocial={onClickContinueWithSocial}
                    logoUrl={logoUrl}
                  />
                </TabPanel>
              )}
              <TabPanel id={ClaimContainerType.EXISTING_WALLET}>
                <ConnectExistingWalletContainer
                  onClickMetamask={onClickMetamask}
                  onClickWalletConnect={onClickWalletConnect}
                  walletOptions={walletOptions}
                  logoUrl={logoUrl}
                  onClickDoNotHaveWallet={onClickDoNotHaveWallet}
                  content={content?.ConnectWalletContent}
                />
              </TabPanel>
              <TabPanel id={ClaimContainerType.RECOVERY_CODE}>
                <RecoveryCodeContainer
                  recoveryCode={recoveryCode}
                  logoUrl={logoUrl}
                  error={error}
                  description={content?.RecoveryCodeModal?.description}
                  onChangeRecoveryCode={onChangeRecoveryCode}
                  onClickRecoveryCode={onClickRecoveryCode}
                />
              </TabPanel>
              <TabPanel id={ClaimContainerType.OTP}>
                <PaperWalletOTPContainer
                  content={content?.ConnectWalletContent}
                  email={email}
                  otp={otp}
                  error={error}
                  onChangeOTP={onChangeOTP}
                  onClickRequestNewCode={onClickContinueWithEmail}
                  logoUrl={logoUrl}
                />
              </TabPanel>
              <TabPanel id={ClaimContainerType.CHECK_TOKEN_GATING}>
                <CheckTokenContainer
                  logoUrl={logoUrl}
                  content={content?.TokenGatingContent}
                  tokenName={tokenGatingName}
                  isLoading={showLoader}
                  onClickContinue={onClickContinueCheckToken}
                  onClickSkip={onClickSkip}
                />
              </TabPanel>
              <TabPanel id={ClaimContainerType.NOT_ELIGIBLE_TOKEN_GATING}>
                <NotEligibleTokenContainer
                  logoUrl={logoUrl}
                  content={content?.TokenGatingNotEligibleContent}
                  tokenName={tokenName}
                  tokenGatingError={tokenGatingError}
                  showDisconnectButton={showDisconnectButton}
                  showBuyButton={showBuyButton}
                  onClickBuyToken={onClickBuyToken}
                  onClickDisconnectWallet={onClickDisconnectWallet}
                />
              </TabPanel>
              <TabPanel id={ClaimContainerType.PAY_WITH_GAS}>
                <PayWithGasContainer
                  termsUrl={termsUrl}
                  privacyUrl={privacyUrl}
                  price={price}
                  isLoading={showLoader}
                  validation={validation}
                  networkName={networkName}
                  currency={currency}
                  loaderDescription={loaderDescription}
                  onClickClaimNow={onClickClaimWithGas}
                  onClickSwitchNetwork={onClickSwitchNetwork}
                  content={content?.ClaimWithGasContent}
                />
              </TabPanel>
              <TabPanel id={ClaimContainerType.CLAIM_ERROR}>
                <ClaimErrorContainer
                  logoUrl={logoUrl}
                  content={content?.ClaimErrorContent}
                  error={error}
                  onClickDisconnectWallet={onClickDisconnectWallet}
                />
              </TabPanel>
            </Box>
          </Box>
        )}
      </TabSwitcher>
    </Dialog>
  );
};

export default ClaimModalLayout;
